import { Link, useLocation, useRouter } from "@tanstack/react-router";
import {
  Bell,
  LayoutDashboard,
  LogOut,
  MoreVertical,
  PanelLeft,
  PanelLeftClose,
  UserCircle,
  X,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  getNavForRole,
  type NavChild,
  type NavItem,
  type PbosRole,
} from "@/components/layout/nav-items";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNotifications } from "@/hooks/use-notifications";
import { signOut, useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

const COLLAPSED_KEY = "mobile-sidebar-collapsed";

function getInitials(name: string | undefined): string {
  if (!name) return "U";
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  return (parts[0]?.[0] ?? "U").toUpperCase();
}

function readStoredCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === "true";
  } catch {
    return false;
  }
}

interface MobileSidebarProps {
  role: PbosRole;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function MobileSidebar({ role, mobileOpen, onMobileClose }: MobileSidebarProps) {
  const { data: session } = useSession();
  const { data: notificationList } = useNotifications();
  const location = useLocation();
  const router = useRouter();
  const [collapsed, setCollapsed] = useState<boolean>(() => readStoredCollapsed());

  const navItems = getNavForRole(role);
  const homePath = role === "supervisor" ? "/daily-logs" : "/";
  const showMyCompany = role === "contractor";

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, String(collapsed));
    } catch {
      // ignore storage errors
    }
  }, [collapsed]);

  const unreadCount = notificationList?.filter((n) => !n.read).length ?? 0;

  const handleSignOut = useCallback(async () => {
    await signOut();
    router.navigate({ to: "/login" });
  }, [router]);

  const isActive = useCallback(
    (path: string) => {
      if (path === "/") return location.pathname === "/";
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    },
    [location.pathname],
  );

  function renderLeafLink(child: NavChild, expanded: boolean, indented: boolean) {
    const active = isActive(child.path);
    const link = (
      <Link
        to={child.path}
        onClick={onMobileClose}
        className={cn(
          "flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          active ? "bg-white/10 text-white" : "text-white/60 hover:bg-white/5 hover:text-white",
          !expanded && "justify-center",
          expanded && indented && "ml-2",
        )}
      >
        <child.icon className="h-4 w-4 shrink-0" />
        {expanded && <span>{child.label}</span>}
      </Link>
    );

    if (!expanded) {
      return (
        <Tooltip key={child.path} delayDuration={0}>
          <TooltipTrigger asChild>{link}</TooltipTrigger>
          <TooltipContent side="right" className="border-gray-700 bg-gray-900 text-white">
            {child.label}
          </TooltipContent>
        </Tooltip>
      );
    }
    return <div key={child.path}>{link}</div>;
  }

  function renderNavItem(item: NavItem, expanded: boolean) {
    if (item.path) {
      return renderLeafLink(
        { label: item.label, icon: item.icon, path: item.path },
        expanded,
        false,
      );
    }
    if (!item.children || item.children.length === 0) return null;

    if (!expanded) {
      // Collapsed sidebar: render children flat with tooltips, no header.
      return (
        <div key={item.label} className="space-y-0.5">
          {item.children.map((c) => renderLeafLink(c, expanded, false))}
        </div>
      );
    }

    return (
      <div key={item.label} className="pt-2">
        <div className="flex items-center gap-2 px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
          <item.icon className="h-3 w-3 shrink-0" />
          <span>{item.label}</span>
        </div>
        <div className="space-y-0.5">
          {item.children.map((c) => renderLeafLink(c, expanded, true))}
        </div>
      </div>
    );
  }

  function renderBrand(expanded: boolean, showCloseButton: boolean) {
    return (
      <div className="flex h-28 items-center justify-between gap-2 border-b border-white/10 px-3">
        {expanded ? (
          <Link
            to={homePath}
            onClick={onMobileClose}
            className="flex min-w-0 flex-1 flex-col items-center justify-center gap-1 rounded py-2 hover:bg-white/5"
          >
            <img
              src="/logo-white.png"
              alt="Prominent Builders"
              className="h-14 w-full max-w-[200px] object-contain"
            />
            <span className="text-sm font-semibold tracking-[0.25em] text-white/80">BOS</span>
          </Link>
        ) : (
          <Link
            to={homePath}
            onClick={onMobileClose}
            className="flex min-w-0 flex-1 items-center justify-center rounded p-1 hover:bg-white/5"
          >
            <img
              src="/logo-white.png"
              alt="Prominent Builders"
              className="h-8 w-8 object-contain"
            />
          </Link>
        )}
        {showCloseButton ? (
          <button
            type="button"
            onClick={onMobileClose}
            className="shrink-0 rounded p-1 text-white/60 hover:text-white"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            className="shrink-0 rounded p-1 text-white/60 hover:text-white"
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        )}
      </div>
    );
  }

  function renderNotificationsRow(expanded: boolean) {
    const content = (
      <Link
        to="/notifications"
        onClick={onMobileClose}
        className={cn(
          "relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors",
          "text-white/60 hover:bg-white/5 hover:text-white",
          !expanded && "justify-center",
        )}
      >
        <Bell className="h-4 w-4 shrink-0" />
        {expanded && <span className="flex-1">Notifications</span>}
        {unreadCount > 0 && (
          <span
            className={cn(
              "flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white",
              !expanded && "absolute right-1 top-1 h-4 min-w-[16px]",
            )}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </Link>
    );

    if (!expanded) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="right" className="border-gray-700 bg-gray-900 text-white">
            Notifications
          </TooltipContent>
        </Tooltip>
      );
    }
    return content;
  }

  function renderUserMenu(expanded: boolean) {
    if (!session?.user) return null;
    return (
      <div className="border-t border-white/10 p-3">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={cn(
                "flex w-full items-center gap-3 rounded-md p-1 text-left transition-colors hover:bg-white/5",
                !expanded && "justify-center",
              )}
            >
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white/20 text-xs font-medium text-white">
                {getInitials(session.user.name ?? undefined)}
              </div>
              {expanded && (
                <>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{session.user.name}</p>
                    <p className="truncate text-xs text-white/50">
                      {(() => {
                        const u = session.user as Record<string, unknown>;
                        const companyName =
                          typeof u.companyName === "string" && u.companyName.trim() !== ""
                            ? u.companyName
                            : null;
                        return (
                          companyName ?? <span className="capitalize">{session.user.role}</span>
                        );
                      })()}
                    </p>
                  </div>
                  <MoreVertical className="h-4 w-4 shrink-0 text-white/50" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="top" align="start" className="w-56">
            <DropdownMenuItem asChild>
              <Link to="/profile" onClick={onMobileClose} className="cursor-pointer">
                <UserCircle className="h-4 w-4" />
                <span>Profile</span>
              </Link>
            </DropdownMenuItem>
            {showMyCompany && (
              <DropdownMenuItem asChild>
                <Link to="/my-company" onClick={onMobileClose} className="cursor-pointer">
                  <LayoutDashboard className="h-4 w-4" />
                  <span>My Company</span>
                </Link>
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={handleSignOut} className="cursor-pointer">
              <LogOut className="h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  function renderSidebar(expanded: boolean, showCloseButton: boolean) {
    return (
      <aside
        className={cn(
          "flex h-full flex-col overflow-hidden bg-[#1a2332] transition-all duration-200",
          expanded ? "w-64" : "w-16",
        )}
      >
        {renderBrand(expanded, showCloseButton)}
        <nav className="flex-1 space-y-0.5 overflow-y-auto p-2">
          {navItems.map((item) => renderNavItem(item, expanded))}
          <div className="pt-2">{renderNotificationsRow(expanded)}</div>
        </nav>
        {renderUserMenu(expanded)}
      </aside>
    );
  }

  return (
    <TooltipProvider>
      {/* Desktop sidebar — collapsible */}
      <div className="hidden md:flex md:h-full">{renderSidebar(!collapsed, false)}</div>

      {/* Mobile drawer — always expanded labels */}
      {mobileOpen && (
        <>
          <button
            type="button"
            aria-label="Close menu overlay"
            className="fixed inset-0 z-40 bg-black/50 md:hidden"
            onClick={onMobileClose}
          />
          <div className="fixed left-0 top-0 z-50 h-full md:hidden">
            {renderSidebar(true, true)}
          </div>
        </>
      )}
    </TooltipProvider>
  );
}
